<template>
  <div class="container-fluid py-4">
    <!-- Header Section (similar to LeaveTypes) -->
    <div class="row mb-4">
      <div class="col-md-8">
        <div class="d-flex align-items-center">
          <div class="bg-primary-light p-3 rounded-2 me-3">
            <vue-feather type="file-text" class="d-flex justify-content-center align-content-center" stroke="#652aa3" size="24"></vue-feather>
          </div>
          <h3 class="mb-0 fw-bold">Review Templates</h3>
        </div>
      </div>
      <div class="col-md-4">
        <div class="d-flex justify-content-end">
          <button
            v-if="profile.role !== 'user'"
            class="btn btn-primary shadow-sm"
            @click="openCreate"
          >
            <vue-feather type="plus" size="18" class="me-1"></vue-feather>
            Add Review Template
          </button>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <Card cardClasses="shadow-sm border-0 h-100 mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div class="font-size-125 color-gray-600">
              <vue-feather type="file-text" stroke="#652aa3" size="18" class="me-1"></vue-feather>
              Review Templates ({{ templates.length }})
            </div>
            <UInput v-model="dataTableSearch" size="md" placeholder="Search template" class="mb-0 w-25 search-input"/>
          </div>

          <!-- Loading Spinner -->
          <div v-if="loading" class="text-center py-4">
            <Spinner spinner-class="mb-1" />
          </div>

          <!-- Templates Table -->
          <div v-if="!loading && filteredTemplates.length > 0" class="table-responsive">
            <table class="table table-hover align-middle">
              <thead class="table-light">
                <tr>
                  <th scope="col" class="fw-semibold">Name</th>
                  <th scope="col" class="fw-semibold">Sections</th>
                  <th scope="col" class="fw-semibold">Default</th>
                  <th scope="col" class="fw-semibold">Description</th>
                  <th scope="col" class="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="template in filteredTemplates" :key="template.id">
                  <td class="fw-semibold">{{ template.name }}</td>
                  <td>
                    <div v-if="template.sections && template.sections.length">
                      <span v-for="(sec, i) in template.sections" :key="i" class="badge bg-light text-dark me-1 mb-1">
                        {{ sec.title }}
                      </span>
                    </div>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td>
                    <span v-if="template.is_default" class="badge bg-success">Default</span>
                    <span v-else class="badge bg-secondary">No</span>
                  </td>
                  <td class="text-muted">{{ template.description || '-' }}</td>
                  <td class="text-end">
                    <router-link :to="`/review-template/${template.id}`" class="btn btn-sm btn-outline-info me-2">
                      <vue-feather type="eye" size="14"></vue-feather>
                    </router-link>
                    <button class="btn btn-sm btn-outline-success me-2" v-if="!template.is_default" @click="setDefault(template)">
                      <vue-feather type="star" size="14"></vue-feather>
                    </button>
                    <button class="btn btn-sm btn-outline-primary me-2" @click="openEdit(template)">
                      <vue-feather type="edit" size="14"></vue-feather>
                    </button>
                    <button class="btn btn-sm btn-outline-danger" @click="confirmDelete(template)">
                      <vue-feather type="trash" size="14"></vue-feather>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Empty State -->
          <div v-if="!loading && filteredTemplates.length === 0" class="text-center py-5">
            <vue-feather type="file-text" size="48" class="mb-3" stroke="#652aa3"></vue-feather>
            <h5>No Review Templates Found</h5>
            <p class="text-muted">Create your first review template to get started.</p>
            <button class="btn btn-primary" @click="openCreate">
              <vue-feather type="plus" size="16" class="me-1"></vue-feather>
              Add Review Template
            </button>
          </div>
        </Card>
      </div>
    </div>

    <!-- Add/Edit Template Modal -->
    <Modal
      :open="showModal"
      @closed="closeModal"
      dialog-size="modal-lg"
    >
      <div class="modal-header border-0">
        <h5 class="modal-title m-0 d-flex align-items-center">
          {{ isEditing ? 'Edit Review Template' : 'Add Review Template' }}
        </h5>
        <button @click="closeModal" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="save">
              <div class="row g-3">
                <div class="col-md-6">
                  <label class="form-label">Name</label>
                  <input v-model="form.name" type="text" class="form-control" placeholder="e.g. Mid-Year Performance Review" required />
                </div>
                <div class="col-md-6">
                  <label class="form-label">Type</label>
                  <select v-model="form.type" class="form-select" required>
                    <option value="core">Core</option>
                    <option value="technical">Technical</option>
                    <option value="behavioral">Behavioral</option>
                    <option value="goal">Goal</option>
                  </select>
                </div>
<!--                <div class="col-md-3">-->
<!--                  <label class="form-label">Weight</label>-->
<!--                  <input v-model.number="form.weight" type="number" min="1" max="10" class="form-control" required />-->
<!--                </div>-->
                <div class="col-12">
                  <div class="form-check">
                    <input type="checkbox" class="form-check-input" id="is_default" v-model="form.is_default">
                    <label class="form-check-label" for="is_default">Set as Default Template</label>
                  </div>
                </div>
                <div class="col-12">
                  <label class="form-label">Description</label>
                  <textarea v-model="form.description" class="form-control" rows="2" placeholder="Short description"></textarea>
                </div>
              </div>

              <div class="mt-4">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <h6 class="mb-0">Sections</h6>
                  <div>
                    <button type="button" class="btn btn-sm btn-outline-secondary me-2" @click="useSampleData">
                      Use sample data
                    </button>
                    <button type="button" class="btn btn-sm btn-primary" @click="addSection">
                      <i class="icon-feather-plus me-1"></i> Add Section
                    </button>
                  </div>
                </div>

                <div v-if="!form.sections.length" class="text-muted">No sections yet. Click "Add Section" to start.</div>

                <div v-for="(section, sIndex) in form.sections" :key="sIndex" class="card mb-3">
                  <div class="card-body">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                      <h6 class="mb-0">Section {{ sIndex + 1 }}</h6>
                      <button type="button" class="btn btn-sm btn-outline-danger" @click="removeSection(sIndex)">
                        <i class="bi bi-trash"></i>
                      </button>
                    </div>

                    <div class="row g-3">
                      <div class="col-md-6">
                        <label class="form-label">Title</label>
                        <input v-model="section.title" type="text" class="form-control" placeholder="e.g. Core Competencies" required />
                      </div>
                      <div class="col-md-6">
                        <label class="form-label">Description</label>
                        <input v-model="section.description" type="text" class="form-control" placeholder="Describe the focus of this section" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

          <div class="d-flex justify-content-end">
            <button type="button" class="btn btn-outline-secondary me-2" @click="closeModal">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              {{ isEditing ? 'Update' : 'Save' }}
            </button>
          </div>
        </form>
      </div>
    </Modal>

    <!-- View Template Modal -->
    <Modal
      :open="showViewModal"
      @closed="closeView"
      dialog-size="modal-lg"
    >
      <div class="modal-header border-0">
        <h5 class="modal-title m-0 d-flex align-items-center">
          Template Details
        </h5>
        <button @click="closeView" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <div v-if="activeTemplate">
          <div class="mb-3">
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <h5 class="mb-1">{{ activeTemplate.name }}</h5>
                <div class="d-flex align-items-center gap-2 flex-wrap">
                  <span v-if="activeTemplate.is_default" class="badge bg-success me-2">Default</span>
                  <span class="badge bg-primary me-2">{{ activeTemplate.type ? activeTemplate.type.charAt(0).toUpperCase() + activeTemplate.type.slice(1) : 'Core' }}</span>
                  <span class="badge bg-secondary me-2">Weight: {{ activeTemplate.weight || 1 }}</span>
                </div>
              </div>
            </div>
            <p class="mt-2 mb-0 text-muted">{{ activeTemplate.description || 'No description' }}</p>
          </div>

          <div>
            <h6 class="fw-semibold">Sections</h6>
            <div v-if="activeTemplate.sections && activeTemplate.sections.length">
              <div v-for="(sec, idx) in activeTemplate.sections" :key="idx" class="border rounded-3 p-3 mb-2">
                <div class="d-flex justify-content-between align-items-start">
                  <div>
                    <div class="fw-semibold">{{ sec.title }}</div>
                    <div class="text-muted small">{{ sec.description || '-' }}</div>
                  </div>
                  <span class="badge bg-light text-dark">{{ (sec.categories || []).length }} categories</span>
                </div>
                <div class="mt-2">
                  <span
                    v-for="(cat, c) in (sec.categories || [])"
                    :key="c"
                    :class="categoryClass(c)"
                  >{{ cat }}</span>
                </div>
              </div>
            </div>
            <div v-else class="text-muted">No sections</div>
          </div>
        </div>
      </div>
      <div class="modal-footer border-0">
        <button type="button" class="btn btn-outline-secondary" @click="closeView">Close</button>
      </div>
    </Modal>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import reviewTemplateService from '@/api/reviewTemplateService'
import { decrypt } from '@/utils/crypto'
import notify from '@/utils/notify'
import confirmDialog from '@/utils/confirmDialog'
import VueFeather from 'vue-feather'

export default {
  name: 'ReviewTemplates',
  components: { VueFeather },
  setup() {
    const loading = ref(true)
    const saving = ref(false)
    const templates = ref([])
    const dataTableSearch = ref('')

    const showModal = ref(false)
    const isEditing = ref(false)
    const editingId = ref(null)

    const profile = ref({})

    // View modal state
    const showViewModal = ref(false)
    const activeTemplate = ref(null)

    // Colors for category chips in View modal
    const categoryPalette = ['primary', 'success', 'info', 'warning', 'danger', 'secondary', 'dark']
    const categoryClass = (index) => {
      const color = categoryPalette[index % categoryPalette.length]
      const lightBg = ['warning', 'light'].includes(color)
      const textClass = lightBg ? 'text-dark' : 'text-white'
      return ['badge', `bg-${color}`, textClass, 'me-1', 'mb-1']
    }

    const defaultForm = () => ({
      name: '',
      description: '',
      type: 'core',
      weight: 100,
      sections: [],
      is_default: false,
    })

    const form = ref(defaultForm())

    const refresh = async () => {
      try {
        loading.value = true
        const data = await reviewTemplateService.listTemplates()
        templates.value = Array.isArray(data?.data) ? data.data : (data || [])
      } catch (e) {
        notify.alert.error('Failed to fetch review templates')
        // eslint-disable-next-line no-console
        console.error(e)
      } finally {
        loading.value = false
      }
    }

    const filteredTemplates = computed(() => {
      if (!dataTableSearch.value) return templates.value
      const search = dataTableSearch.value.toLowerCase()
      return templates.value.filter(tpl => {
        const inName = (tpl.name || '').toLowerCase().includes(search)
        const inDesc = (tpl.description || '').toLowerCase().includes(search)
        const inSections = Array.isArray(tpl.sections) && tpl.sections.some(s => (s.title || '').toLowerCase().includes(search))
        return inName || inDesc || inSections
      })
    })

    const openCreate = () => {
      isEditing.value = false
      editingId.value = null
      form.value = defaultForm()
      showModal.value = true
    }

    const openView = (tpl) => {
      // If needed, we could fetch latest details: reviewTemplateService.getTemplate(tpl.id)
      activeTemplate.value = {
        id: tpl.id,
        name: tpl.name,
        description: tpl.description,
        type: tpl.type || 'core',
        weight: tpl.weight || 100,
        is_default: tpl.is_default,
        sections: Array.isArray(tpl.sections) ? tpl.sections : []
      }
      showViewModal.value = true
    }

    const closeView = () => {
      showViewModal.value = false
      activeTemplate.value = null
    }

    const openEdit = (tpl) => {
      isEditing.value = true
      editingId.value = tpl.id
      form.value = {
        name: tpl.name || '',
        description: tpl.description || '',
        type: tpl.type || 'core',
        weight: tpl.weight || 100,
        sections: (tpl.sections || []).map(s => ({
          title: s.title || '',
          description: s.description || '',
          categories: Array.isArray(s.categories) ? [...s.categories] : [],
        })),
        is_default: !!tpl.is_default,
      }
      showModal.value = true
    }

    const closeModal = () => {
      if (!saving.value) showModal.value = false
    }

    const addSection = () => {
      form.value.sections.push({ title: '', description: '', categories: [] })
    }

    const removeSection = (index) => {
      form.value.sections.splice(index, 1)
    }

    const addCategory = (sIndex) => {
      form.value.sections[sIndex].categories.push('')
    }

    const removeCategory = (sIndex, cIndex) => {
      form.value.sections[sIndex].categories.splice(cIndex, 1)
    }

    const useSampleData = () => {
      form.value = {
        name: 'Mid-Year Performance Review',
        description: 'Covers core skills and goals progress',
        type: 'core',
        weight: 100,
        is_default: false,
        sections: [
          {
            title: 'Core Competencies',
            description: 'Evaluate fundamental job skills',
            categories: ['Work Quality', 'Productivity', 'Communication']
          },
          {
            title: 'Goals & Objectives',
            description: 'Progress against planned goals',
            categories: ['Goal Achievement', 'Initiative', 'Growth']
          }
        ]
      }
    }

    const save = async () => {
      saving.value = true
      try {
        const payload = JSON.parse(JSON.stringify(form.value))
        if (!isEditing.value) {
          await reviewTemplateService.createTemplate(payload)
        } else {
          await reviewTemplateService.updateTemplate(editingId.value, payload)
        }
        showModal.value = false
        await refresh()
      } finally {
        saving.value = false
      }
    }

    const confirmDelete = (tpl) => {
      confirmDialog('Review Template', async () => {
        try {
          saving.value = true
          await reviewTemplateService.deleteTemplate(tpl.id)
          await refresh()
        } catch (e) {
          notify.alert.error('Failed to delete template')
        } finally {
          saving.value = false
        }
      })
    }

    const setDefault = async (tpl) => {
      saving.value = true
      try {
        await reviewTemplateService.setAsDefault(tpl.id)
        await refresh()
      } finally {
        saving.value = false
      }
    }

    onMounted(async () => {
      try {
        profile.value = JSON.parse(decrypt(sessionStorage.getItem('profile')))
      } catch (e) {
        profile.value = { role: 'user' }
      }
      await refresh()
    })

    return {
      loading,
      saving,
      templates,
      dataTableSearch,
      filteredTemplates,
      showModal,
      isEditing,
      editingId,
      form,
      profile,
      showViewModal,
      activeTemplate,
      // actions
      refresh,
      openCreate,
      openEdit,
      openView,
      closeView,
      closeModal,
      addSection,
      removeSection,
      addCategory,
      removeCategory,
      useSampleData,
      save,
      confirmDelete,
      setDefault,
      categoryClass,
    }
  }
}
</script>

<style scoped>
.page-title {
  font-weight: 600;
}
.badge + .badge {
  margin-left: 0.25rem;
}
</style>
